import type { FieldKeyString, FieldValue, FieldVersion, Hlc, Uuid } from '@colplat/protocol';
import { parseFieldKey } from '@colplat/protocol';

/**
 * Field-mode reconnect review needs no new protocol message: the existing
 * `rejected{key, reason:'stale-base', current}` already carries what
 * ADR-0011 needs — auto-apply non-conflicting changes, surface stale-base
 * ones for explicit review. Document-mode pending revisions travel over REST
 * instead (`sync` must never do transactional work — ADR-0001), fetched here
 * on `openRecord`.
 */

export interface StaleBaseReview {
  key: FieldKeyString;
  mine: FieldValue;
  mineClock: Hlc;
  theirs: FieldVersion;
  detectedAt: string;
  origin: 'offline-replay' | 'online-race';
}

export interface PendingRevision {
  id: Uuid;
  entity: string;
  recordId: Uuid;
  baseVersion: number;
  /** The attempted whole-record snapshot. */
  payload: Record<string, FieldValue>;
  /** The current server snapshot, for a side-by-side diff. */
  current: Record<string, FieldValue>;
  reason: 'stale-base' | 'period-closed' | 'validation';
  createdAt: string;
}

export interface ReviewStoreOptions {
  fetchPendingRevisions: (entity: string, recordId: Uuid) => Promise<PendingRevision[]>;
  postPendingRevisionResolution: (id: Uuid, resolution: 'applied' | 'discarded') => Promise<void>;
  now?: () => string;
}

export class ReviewStore {
  private readonly staleBase = new Map<FieldKeyString, StaleBaseReview>();
  private readonly pendingRevisions = new Map<string, PendingRevision[]>();
  private readonly listeners = new Set<() => void>();

  constructor(private readonly opts: ReviewStoreOptions) {}

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  addStaleBase(item: Omit<StaleBaseReview, 'detectedAt'>): void {
    const now = this.opts.now ?? (() => new Date().toISOString());
    this.staleBase.set(item.key, { ...item, detectedAt: now() });
    this.notify();
  }

  listStaleBase(entity?: string, recordId?: Uuid): StaleBaseReview[] {
    const all = [...this.staleBase.values()];
    if (entity === undefined) return all;
    return all.filter((r) => {
      const parsed = parseFieldKey(r.key);
      return parsed.entity === entity && (recordId === undefined || parsed.recordId === recordId);
    });
  }

  /**
   * Returns what `session.commitAndRelease` must send, or null when nothing
   * needs sending. Rebases onto the CURRENT version (`theirs.version`),
   * never the original stale base — that is the entire point of this file.
   */
  resolveStaleBase(key: FieldKeyString, keep: 'mine' | 'theirs'): { value: FieldValue; base: number } | null {
    const item = this.staleBase.get(key);
    if (!item) return null;
    this.staleBase.delete(key);
    this.notify();
    if (keep === 'theirs') return null; // the server's value is already current, nothing to send
    return { value: item.mine, base: item.theirs.version };
  }

  async loadPendingRevisions(entity: string, recordId: Uuid): Promise<void> {
    const items = await this.opts.fetchPendingRevisions(entity, recordId);
    this.pendingRevisions.set(`${entity}:${recordId}`, items);
    this.notify();
  }

  listPendingRevisions(entity: string, recordId: Uuid): PendingRevision[] {
    return this.pendingRevisions.get(`${entity}:${recordId}`) ?? [];
  }

  async resolvePendingRevision(id: Uuid, resolution: 'applied' | 'discarded'): Promise<void> {
    await this.opts.postPendingRevisionResolution(id, resolution);
    for (const [key, items] of this.pendingRevisions) {
      this.pendingRevisions.set(key, items.filter((i) => i.id !== id));
    }
    this.notify();
  }
}
